import { useParams } from "react-router-dom";
import { useSeoMeta } from "@unhead/react";
import { Check, Loader2, Rocket, XCircle } from "lucide-react";

import { SiteHeader } from "@/components/cashcraft/SiteHeader";
import { SiteFooter } from "@/components/cashcraft/SiteFooter";
import { Card, CardContent } from "@/components/ui/card";
import { useOrderStatus } from "@/hooks/useOrderStatus";
import { cn } from "@/lib/utils";
import { ORDER_STATUS_META, type OrderStatus } from "@/lib/cashcraft";

/** The happy-path stages shown in the timeline, in order. */
const TIMELINE: { key: OrderStatus; label: string; blurb: string }[] = [
  { key: "new", label: "Order received", blurb: "We've got your request." },
  { key: "deposit_paid", label: "Deposit paid", blurb: "Deposit confirmed on-chain." },
  { key: "building", label: "Building", blurb: "We're crafting your site." },
  { key: "delivered", label: "Delivered", blurb: "Your site is ready to launch." },
  { key: "paid_in_full", label: "Paid in full", blurb: "All settled — thank you!" },
];

const TIMELINE_INDEX: Record<OrderStatus, number> = {
  new: 0,
  deposit_paid: 1,
  building: 2,
  delivered: 3,
  paid_in_full: 4,
  cancelled: -1,
};

const Track = () => {
  const { id } = useParams<{ id: string }>();
  const { data: status, isLoading } = useOrderStatus(id);

  useSeoMeta({
    title: "Track your project · BCH Launch",
    description: "Follow your website build from order to launch.",
  });

  const current = status?.status ?? "new";
  const currentIndex = TIMELINE_INDEX[current];
  const cancelled = current === "cancelled";

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <SiteHeader />
      <main className="flex-1">
        <div className="container mx-auto max-w-2xl px-4 py-14 sm:px-6">
          <div className="mb-8 text-center">
            <div className="mx-auto mb-4 inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/10 px-4 py-1.5 text-sm font-medium text-primary">
              <Rocket className="h-4 w-4" />
              Project tracker
            </div>
            <h1 className="text-3xl font-bold tracking-tight sm:text-4xl">
              Track your project
            </h1>
            <p className="mt-3 text-muted-foreground">
              Bookmark this page to follow your build from order to launch.
            </p>
          </div>

          <Card className="border-border/60">
            <CardContent className="p-6 sm:p-8">
              {isLoading ? (
                <div className="flex items-center justify-center gap-2 py-12 text-muted-foreground">
                  <Loader2 className="h-5 w-5 animate-spin" />
                  Loading status…
                </div>
              ) : cancelled ? (
                <div className="flex flex-col items-center gap-3 py-10 text-center">
                  <XCircle className="h-10 w-10 text-destructive" />
                  <h2 className="text-xl font-bold">Order cancelled</h2>
                  <p className="max-w-sm text-muted-foreground">
                    This project has been marked cancelled. If you think this is
                    a mistake, get in touch and we'll sort it out.
                  </p>
                </div>
              ) : (
                <>
                  <div className="mb-6 text-center">
                    <div className="text-sm text-muted-foreground">
                      Current status
                    </div>
                    <div className="mt-1 text-2xl font-bold text-primary">
                      {ORDER_STATUS_META[current].label}
                    </div>
                    {status?.note && (
                      <p className="mt-2 text-sm text-muted-foreground">
                        “{status.note}”
                      </p>
                    )}
                  </div>

                  <ol className="relative space-y-6 pl-2">
                    {TIMELINE.map((step, i) => {
                      const done = i < currentIndex;
                      const active = i === currentIndex;
                      const isLast = i === TIMELINE.length - 1;
                      return (
                        <li key={step.key} className="relative flex gap-4">
                          {!isLast && (
                            <span
                              aria-hidden
                              className={cn(
                                "absolute left-[15px] top-8 h-[calc(100%+0.5rem)] w-0.5",
                                done ? "bg-primary" : "bg-border",
                              )}
                            />
                          )}
                          <span
                            className={cn(
                              "relative z-10 flex h-8 w-8 shrink-0 items-center justify-center rounded-full border-2 transition-colors",
                              done && "border-primary bg-primary text-primary-foreground",
                              active && "border-primary bg-primary/15 text-primary",
                              !done && !active && "border-border bg-muted text-muted-foreground",
                            )}
                          >
                            {done ? (
                              <Check className="h-4 w-4" />
                            ) : (
                              <span className="text-xs font-bold">{i + 1}</span>
                            )}
                          </span>
                          <div className="pt-0.5">
                            <div
                              className={cn(
                                "font-semibold",
                                active && "text-primary",
                                !done && !active && "text-muted-foreground",
                              )}
                            >
                              {step.label}
                            </div>
                            <div className="text-sm text-muted-foreground">
                              {step.blurb}
                            </div>
                          </div>
                        </li>
                      );
                    })}
                  </ol>
                </>
              )}
            </CardContent>
          </Card>

          <p className="mt-6 text-center text-xs text-muted-foreground">
            This page shows only your project's stage — no private details are
            ever shown here. Status updates automatically.
          </p>

          <div className="mt-4 rounded-xl border border-dashed border-border bg-muted/20 p-4 text-center text-xs text-muted-foreground">
            <strong className="text-foreground">Got revisions?</strong> Once
            delivered, send all your changes together as one batch — that's one
            revision round. Gathering them up gets you the fastest turnaround.
          </div>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
};

export default Track;
